import {useMemo, useState} from 'react'
import {useDialogStore} from '@ariakit/react'
import dayjs from 'dayjs'
import {AxiosError} from 'axios'
import {
    Dialog,
    DialogDismiss,
    DialogHeader,
    DialogHeading,
    DialogProvider
} from '@/components/ui/dialog-atoms/DialogAtoms'
import {Button} from '@/components/ui/button/Button'
import {Badge} from '@/components/ui/badge/Badge'
import {Checkbox} from '@/components/commons/checkbox/ChekBox'
import {Spinner} from '@/components/ui/spinner/Spinner'
import {AlertCircleIcon, CheckCircleIcon, Download01Icon, Send01Icon, Trash01Icon} from '@/components/ui/icon/Icon'
import {InputDate} from '@/components/commons/input-date/InputDate'
import {useTranslation} from '@/translations/i18n'
import {formatLocaleDate} from '@/dayjs'
import {toast} from '@/utilities/toast/toast'
import {buildFile, downloadFile} from '@/utilities/helpers'
import {BulkSiarExportPreviewTableSkeleton} from '@/features/projects/siar-bulk-export/components/bulk-siar-export-dialog/BulkSiarExportPreviewTableSkeleton'
import {useGetSiarExportDetail} from '@/features/projects/siar-bulk-export/queries/useGetSiarExportDetail'
import {useConfirmSiarExportTransmission} from '@/features/projects/siar-bulk-export/queries/useConfirmSiarExportTransmission'
import {useRemoveProjectFromSiarExport} from '@/features/projects/siar-bulk-export/queries/useRemoveProjectFromSiarExport'
import {httpReDownloadSiarExport} from '@/features/projects/siar-bulk-export/http/siarBulkExport.http'
import {SiarExportDetail} from '@/features/projects/siar-bulk-export/types'
import {
    siarExportStatusToBadgeVariant,
    siarExportStatusToLabel,
    siarExportTypeToLabel
} from '@/features/projects/siar-bulk-export/utils'
import {SiarExportDetailTable} from './SiarExportDetailTable'
import {
    StyledSiarExportDetailActionsRow,
    StyledSiarExportDetailActionsSpacer,
    StyledSiarExportDetailDialogBody,
    StyledSiarExportDetailFooter,
    StyledSiarExportDetailHeading,
    StyledSiarExportDetailMetaLeft,
    StyledSiarExportDetailMetaRow,
    StyledSiarExportDetailSelectionInfo,
    StyledSiarExportDetailSubheading,
    StyledSiarExportDetailTextButton,
    StyledSiarExportDetailTransmissionForm,
    StyledSiarExportDetailTransmissionMessage
} from './style'

type SiarExportDetailDialogProps = {
    exportId: string
    isOpen: boolean
    onClose: () => void
}

export const SiarExportDetailDialog = ({exportId, isOpen, onClose}: SiarExportDetailDialogProps) => {
    const dialogStore = useDialogStore({
        open: isOpen,
        setOpen: nextOpen => {
            if (!nextOpen) onClose()
        }
    })

    return (
        <DialogProvider store={dialogStore}>
            <Dialog width="960px">
                {isOpen && <SiarExportDetailDialogShell exportId={exportId} onClose={onClose} />}
            </Dialog>
        </DialogProvider>
    )
}

const formatPeriod = (dateFrom: string | null, dateTo: string | null) => {
    if (!dateFrom && !dateTo) return null
    const from = dateFrom ? formatLocaleDate(dateFrom, 'll') : null
    const to = dateTo ? formatLocaleDate(dateTo, 'll') : null
    if (from && to) return `${from} → ${to}`
    return from ?? to
}

const SiarExportDetailDialogShell = ({exportId, onClose}: {exportId: string; onClose: () => void}) => {
    const {t} = useTranslation()
    const detailQuery = useGetSiarExportDetail(exportId, {enabled: !!exportId})

    if (detailQuery.isLoading || !detailQuery.data) {
        return (
            <>
                <DialogHeader>
                    <DialogHeading>{t('projects:bulk_siar_export_title')}</DialogHeading>
                    <DialogDismiss />
                </DialogHeader>
                <StyledSiarExportDetailDialogBody>
                    <BulkSiarExportPreviewTableSkeleton />
                </StyledSiarExportDetailDialogBody>
            </>
        )
    }

    return <SiarExportDetailDialogContent detail={detailQuery.data} onClose={onClose} />
}

const SiarExportDetailDialogContent = ({detail, onClose}: {detail: SiarExportDetail; onClose: () => void}) => {
    const {t} = useTranslation()
    const [selectedProjectIds, setSelectedProjectIds] = useState<Set<string>>(new Set())
    const [isTransmissionFormOpen, setIsTransmissionFormOpen] = useState(false)
    const [isReDownloading, setIsReDownloading] = useState(false)
    const [isRemoving, setIsRemoving] = useState(false)
    const [transmissionDate, setTransmissionDate] = useState<Date>(() => new Date())

    const pendingProjects = useMemo(
        () => detail.projects.filter(p => p.transmissionStatus === 'pending'),
        [detail.projects]
    )
    const pendingCount = pendingProjects.length
    const isFullyTransmitted = pendingCount === 0
    const selectedCount = selectedProjectIds.size

    const isSelected = (projectId: string) => selectedProjectIds.has(projectId)

    const handleToggle = (projectId: string) => {
        setSelectedProjectIds(prev => {
            const next = new Set(prev)
            if (next.has(projectId)) next.delete(projectId)
            else next.add(projectId)
            return next
        })
    }

    const handleSelectAllPending = () => {
        setSelectedProjectIds(new Set(pendingProjects.map(p => p.projectId)))
    }

    const handleDeselectAll = () => {
        setSelectedProjectIds(new Set())
    }

    const handleMasterToggle = () => {
        if (selectedCount === pendingCount && pendingCount > 0) handleDeselectAll()
        else handleSelectAllPending()
    }

    const masterChecked = pendingCount > 0 && selectedCount === pendingCount
    const masterIndeterminate = selectedCount > 0 && selectedCount < pendingCount

    // Re-download
    const handleReDownload = async () => {
        setIsReDownloading(true)
        try {
            const blob = await httpReDownloadSiarExport({id: detail.id})
            const fileName = detail.fileName.replace(/\.zip$/i, '')
            downloadFile(buildFile({extension: 'zip', fileName, data: blob}))
        } catch {
            toast.error(t('errors:default'))
        } finally {
            setIsReDownloading(false)
        }
    }

    // Transmission flow
    const minTransmissionDate = useMemo(() => dayjs(detail.dateFrom).startOf('day'), [detail.dateFrom])
    const maxTransmissionDate = useMemo(() => dayjs().endOf('day'), [])
    const isDateOutOfRange =
        dayjs(transmissionDate).isBefore(minTransmissionDate) || dayjs(transmissionDate).isAfter(maxTransmissionDate)

    const transmissionMutation = useConfirmSiarExportTransmission({
        onSuccess: () => {
            toast.success(t('projects:bulk_siar_export_transmission_success'))
            setSelectedProjectIds(new Set())
            setIsTransmissionFormOpen(false)
        },
        onError: error => {
            const code = (error.response?.data as {code?: string} | undefined)?.code
            if (code === 'ALREADY_FULLY_TRANSMITTED') {
                toast.error(t('projects:bulk_siar_export_detail_transmit_error_already_full'))
            } else if (code === 'NOTHING_TO_TRANSMIT') {
                toast.error(t('projects:bulk_siar_export_detail_transmit_error_nothing'))
            } else if (code === 'INVALID_TRANSMISSION_DATE') {
                toast.error(t('projects:bulk_siar_export_detail_transmit_error_invalid_date'))
            } else {
                toast.error(t('errors:default'))
            }
        }
    })

    const handleOpenTransmissionForm = () => {
        const today = dayjs()
        const initial = today.isBefore(minTransmissionDate) ? minTransmissionDate : today
        setTransmissionDate(initial.toDate())
        setIsTransmissionFormOpen(true)
    }

    const handleCancelTransmission = () => {
        setIsTransmissionFormOpen(false)
    }

    const handleConfirmTransmission = () => {
        if (isDateOutOfRange) return
        transmissionMutation.mutate({
            id: detail.id,
            selectedProjectIds: Array.from(selectedProjectIds),
            exceptedProjectIds: [],
            transmissionDate: dayjs(transmissionDate).format('YYYY-MM-DD')
        })
    }

    // Remove flow
    const removeMutation = useRemoveProjectFromSiarExport()

    const handleRemoveSelected = async () => {
        if (selectedCount === 0) return
        setIsRemoving(true)
        const ids = Array.from(selectedProjectIds)
        try {
            const results = await Promise.allSettled(
                ids.map(projectId => removeMutation.mutateAsync({exportId: detail.id, projectId}))
            )
            const failed = results.filter(r => r.status === 'rejected')
            const succeeded = results.length - failed.length
            const batchDeleted = results.some(r => r.status === 'fulfilled' && r.value === null)
            if (succeeded > 0) {
                toast.success(t('projects:bulk_siar_export_detail_remove_success', {count: succeeded}))
            }
            if (failed.length > 0) {
                const hasAlreadyTransmitted = failed.some(
                    r =>
                        r.status === 'rejected' &&
                        (r.reason as AxiosError<{code?: string}>)?.response?.data?.code === 'ALREADY_TRANSMITTED'
                )
                if (hasAlreadyTransmitted) {
                    toast.error(t('projects:bulk_siar_export_detail_remove_error_already_transmitted'))
                } else {
                    toast.error(t('errors:default'))
                }
            }
            setSelectedProjectIds(new Set())
            if (batchDeleted) {
                onClose()
                return
            }
        } finally {
            setIsRemoving(false)
        }
    }

    const period = formatPeriod(detail.dateFrom, detail.dateTo)
    const isAnyMutating = transmissionMutation.isPending || isRemoving || isReDownloading

    return (
        <>
            <DialogHeader>
                <StyledSiarExportDetailHeading>
                    <DialogHeading>{detail.fileName}</DialogHeading>
                    <StyledSiarExportDetailSubheading>
                        <Badge variant={siarExportStatusToBadgeVariant[detail.status]} size="sm">
                            {detail.status === 'transmitted' ? (
                                <CheckCircleIcon size={12} />
                            ) : detail.status === 'partially_transmitted' ? (
                                <AlertCircleIcon size={12} />
                            ) : (
                                <Send01Icon size={12} />
                            )}
                            {t(siarExportStatusToLabel[detail.status])}
                        </Badge>
                        <span>·</span>
                        <span>{t(siarExportTypeToLabel[detail.exportType])}</span>
                        {period && (
                            <>
                                <span>·</span>
                                <span>{period}</span>
                            </>
                        )}
                        {detail.addRegistry && (
                            <>
                                <span>·</span>
                                <span style={{display: 'flex', alignItems: 'center', gap: 4}}>
                                    {t('projects:bulk_siar_export_include_registry')} <CheckCircleIcon size={14} />
                                </span>
                            </>
                        )}
                    </StyledSiarExportDetailSubheading>
                </StyledSiarExportDetailHeading>
                <DialogDismiss />
            </DialogHeader>
            <StyledSiarExportDetailDialogBody>
                <StyledSiarExportDetailMetaRow>
                    <StyledSiarExportDetailMetaLeft>
                        <span>
                            {t('projects:bulk_siar_export_detail_subheader_exported_at', {
                                date: formatLocaleDate(detail.createdAt, 'lll')
                            })}
                        </span>
                        <span>·</span>
                        <span>
                            {t('projects:bulk_siar_export_detail_subheader_created_by', {
                                firstName: detail.createdBy.firstName,
                                lastName: detail.createdBy.lastName
                            })}
                        </span>
                    </StyledSiarExportDetailMetaLeft>
                    <span>
                        {t('projects:bulk_siar_export_detail_subheader_pri_count', {count: detail.projectCount})}
                    </span>
                </StyledSiarExportDetailMetaRow>

                {!isFullyTransmitted && (
                    <StyledSiarExportDetailActionsRow>
                        <StyledSiarExportDetailSelectionInfo>
                            <Checkbox
                                id="siar-export-detail-actions-master-checkbox"
                                checked={masterChecked}
                                indeterminate={masterIndeterminate}
                                onChange={handleMasterToggle}
                                disabled={pendingCount === 0}
                            />
                            <span>
                                {selectedCount > 0
                                    ? t('projects:bulk_siar_export_detail_counter_selected', {count: selectedCount})
                                    : t('projects:bulk_siar_export_detail_counter_to_transmit', {count: pendingCount})}
                            </span>
                            {pendingCount > 0 && (
                                <StyledSiarExportDetailTextButton
                                    type="button"
                                    onClick={handleSelectAllPending}
                                    disabled={isAnyMutating}
                                >
                                    {t('projects:bulk_siar_export_select_all')}
                                </StyledSiarExportDetailTextButton>
                            )}
                            {selectedCount > 0 && (
                                <StyledSiarExportDetailTextButton
                                    type="button"
                                    onClick={handleDeselectAll}
                                    disabled={isAnyMutating}
                                >
                                    {t('projects:bulk_siar_export_detail_deselect')}
                                </StyledSiarExportDetailTextButton>
                            )}
                        </StyledSiarExportDetailSelectionInfo>
                        <StyledSiarExportDetailActionsSpacer />
                        {selectedCount > 0 && (
                            <Button
                                variant="secondary"
                                onClick={handleRemoveSelected}
                                disabled={isAnyMutating || isTransmissionFormOpen}
                            >
                                <Trash01Icon />
                                {t('projects:bulk_siar_export_detail_remove_selected', {count: selectedCount})}
                                {isRemoving && <Spinner />}
                            </Button>
                        )}
                        <Button variant="secondary" onClick={handleReDownload} disabled={isAnyMutating}>
                            <Download01Icon />
                            {t('projects:bulk_siar_export_detail_re_export')}
                            {isReDownloading && <Spinner />}
                        </Button>
                        <Button
                            variant="primaryNeutral"
                            onClick={handleOpenTransmissionForm}
                            disabled={selectedCount === 0 || isAnyMutating || isTransmissionFormOpen}
                        >
                            <Send01Icon />
                            {selectedCount === 0
                                ? t('projects:bulk_siar_export_detail_select_to_transmit')
                                : t('projects:bulk_siar_export_detail_transmit_selected', {count: selectedCount})}
                        </Button>
                    </StyledSiarExportDetailActionsRow>
                )}

                {isTransmissionFormOpen && (
                    <StyledSiarExportDetailTransmissionForm>
                        <StyledSiarExportDetailTransmissionMessage>
                            <AlertCircleIcon size={16} />
                            {t('projects:bulk_siar_export_detail_transmission_banner', {count: selectedCount})}
                        </StyledSiarExportDetailTransmissionMessage>
                        <InputDate
                            value={transmissionDate}
                            onChange={date => date && setTransmissionDate(date)}
                            placeholder="gg/mm/aaaa"
                            portal
                            disableBeforeDate={minTransmissionDate.toDate()}
                            disableAfterDate={maxTransmissionDate.toDate()}
                            errorMessage={
                                isDateOutOfRange
                                    ? t('projects:bulk_siar_export_transmission_date_out_of_range')
                                    : undefined
                            }
                        />
                        <Button
                            variant="primaryNeutral"
                            onClick={handleConfirmTransmission}
                            disabled={isDateOutOfRange || transmissionMutation.isPending}
                        >
                            {t('projects:bulk_siar_export_detail_transmission_confirm')}
                            {transmissionMutation.isPending && <Spinner />}
                        </Button>
                        <Button
                            variant="ghost"
                            onClick={handleCancelTransmission}
                            disabled={transmissionMutation.isPending}
                        >
                            {t('projects:bulk_siar_export_detail_transmission_cancel')}
                        </Button>
                    </StyledSiarExportDetailTransmissionForm>
                )}

                <SiarExportDetailTable
                    items={detail.projects}
                    isSelected={isSelected}
                    onToggle={handleToggle}
                    onToggleAll={handleMasterToggle}
                    masterChecked={masterChecked}
                    masterIndeterminate={masterIndeterminate}
                />
            </StyledSiarExportDetailDialogBody>
            <StyledSiarExportDetailFooter>
                <Button variant="ghost" onClick={onClose}>
                    {t('projects:bulk_siar_export_detail_close')}
                </Button>
            </StyledSiarExportDetailFooter>
        </>
    )
}
